import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import UserDetails from '@/components/UserDetails';

/**
 * ── /components/user-details ─────────────────────────────────────────────
 *
 * UserDetails — the "Your details" box on the profile screen
 * (`app/profile/index.tsx`, directly under the membership card). Its only
 * real call site passes every field straight off `useSessionUser()`'s
 * `UserProfile` (`src/api/authApi.ts`'s `toUserProfile`), so the three
 * booleans below are reproductions of THAT mapping, not made up:
 *
 *   - areDetailsMissing  = !(firstName && lastName)      — name incomplete
 *   - isEmailMissing     = firstName && !email            — no email on file
 *   - isOnlyEmailVerificationPending
 *                        = firstName && email && !isEmailVerified
 *
 * They're a linear onboarding ladder (complete name → add email → verify
 * email), each rendering its own `PSLinkButton` nudge paragraph that deep
 * links into `/auth/details` or `/auth/email` — never more than one true at
 * once in practice, so each is previewed on its own rather than combined.
 * `name`/`email` are nullable per the component's own docstring: a session
 * can start at mobile-verify alone, so only `mobile` is guaranteed.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function UserDetailsPreview() {
  return (
    <>
      <Head>
        <title>UserDetails · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Complete — every flag false, the steady state once onboarding is
            fully done: name, email, mobile all present, no nudge paragraph. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              complete — name + verified email + mobile, no missing-info nudge
              (the common case on the profile screen)
            </Text>
            <UserDetails
              name="Abhimanyu"
              email="iamabhimanyu08@gmail.com"
              mobile="+91-8619003830"
              areDetailsMissing={false}
              isEmailMissing={false}
              isOnlyEmailVerificationPending={false}
            />
          </YStack>

          {/* socialMediaLink — the optional 4th line, set via /profile/edit.
            Only shows once the account is otherwise complete; it's an
            independent field, not gated by any of the three booleans. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              socialMediaLink — optional 4th line (set on /profile/edit),
              underlined, shown alongside the other three
            </Text>
            <UserDetails
              name="Abhimanyu"
              email="iamabhimanyu08@gmail.com"
              mobile="+91-8619003830"
              socialMediaLink="instagram.com/puttingscene"
              areDetailsMissing={false}
              isEmailMissing={false}
              isOnlyEmailVerificationPending={false}
            />
          </YStack>

          {/* areDetailsMissing — firstName present but lastName isn't (name
            renders as just the first name), so this is step one of the
            ladder: "Please complete your profile to be able to book
            tickets." routes to /auth/details. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              areDetailsMissing — name incomplete (!(firstName && lastName));
              &quot;complete your profile&quot; nudge → /auth/details
            </Text>
            <UserDetails
              name="Abhimanyu"
              email={null}
              mobile="+91-8619003830"
              areDetailsMissing={true}
              isEmailMissing={false}
              isOnlyEmailVerificationPending={false}
            />
          </YStack>

          {/* isEmailMissing — name is complete but no email was ever added,
            so the email line itself is omitted (null-guarded) and step two
            of the ladder shows instead: "add and verify your email" →
            /auth/email. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              isEmailMissing — name complete, email never added (firstName &&
              !email); &quot;add and verify your email&quot; nudge → /auth/email
            </Text>
            <UserDetails
              name="Abhimanyu"
              email={null}
              mobile="+91-8619003830"
              areDetailsMissing={false}
              isEmailMissing={true}
              isOnlyEmailVerificationPending={false}
            />
          </YStack>

          {/* isOnlyEmailVerificationPending — email is on file (so the email
            line DOES render) but the OTP was never completed: final rung of
            the ladder, "verify your email" → /auth/email with different
            headerCopy than the isEmailMissing case above. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              isOnlyEmailVerificationPending — email present but unverified
              (firstName && email && !isEmailVerified); &quot;verify your
              email&quot; nudge → /auth/email
            </Text>
            <UserDetails
              name="Abhimanyu"
              email="iamabhimanyu08@gmail.com"
              mobile="+91-8619003830"
              areDetailsMissing={false}
              isEmailMissing={false}
              isOnlyEmailVerificationPending={true}
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
